import type { OperatorFunction } from 'rxjs';
import { pipe, tap } from 'rxjs';
import type { Graph } from '../../graph';

interface BlockEntry {
    key: string;
    start: number;
    end?: number;
}

/**
 * Renders blocks in a graph based on provided entries with start and end positions.
 *
 * @param {Graph} graph - The graph object.
 * @param {string} identifier - CSS class for entering and finding DOM objects.
 * @param {string} rectCssClass - CSS class used to style SVG rectangles.
 * @param borderCssClass - Optional CSS class for styling border lines.
 * @returns {Observable} - RxJS observable for managing block rendering.
 */
function renderBlocksInGraph(
    graph: Pick<Graph, 'drawArea' | 'graphHeight'>,
    identifier: string,
    rectCssClass: string,
    borderCssClass: string | null = null
): OperatorFunction<readonly BlockEntry[], readonly BlockEntry[]> {
    function addBorderLine<T extends BlockEntry>(
        entries: readonly T[],
        classSuffix: string,
        x: (d: T) => number
    ): void {
        graph.drawArea
            .selectAll<SVGLineElement, BlockEntry>(`.${identifier}-${classSuffix}-border`)
            .data(entries, (d) => d.key)
            .join((enter) =>
                enter
                    .append('line')
                    .attr('class', `${identifier}-${classSuffix}-border ${borderCssClass}`)
                    .attr('y1', 0)
                    .attr('y2', graph.graphHeight)
            )
            .attr('x1', x)
            .attr('x2', x);
    }

    /**
     * Renders blocks based on provided entries.
     *
     * @param {Object[]} entries - Array of objects with 'start' and 'end' positions.
     */
    function renderBlocks(entries: readonly BlockEntry[]): void {
        const entriesWithEnd = entries.map((d) => {
            // Default to 1px wide when:
            // * no end date yet
            // * start date and end date are the same.
            if (!d.end || d.start === d.end) {
                return {
                    ...d,
                    end: d.start + 1,
                };
            }

            return d;
        }) as Required<BlockEntry>[];
        graph.drawArea
            .selectAll<SVGRectElement, BlockEntry>(`.${identifier}`)
            .data(entriesWithEnd, (d) => d.key)
            .join((enter) =>
                enter
                    .append('rect')
                    .attr('class', `${identifier} ${rectCssClass}`)
                    .attr('y', 0)
                    .attr('height', graph.graphHeight)
            )
            .attr('x', (d) => d.start)
            .attr('width', (d) => d.end - d.start);

        if (borderCssClass !== null) {
            addBorderLine(entries, 'left', (d) => d.start);
            addBorderLine(entriesWithEnd, 'right', (d) => d.end);
        }
    }

    return pipe(
        tap({
            next(entries) {
                renderBlocks(entries);
            },
            unsubscribe() {
                // On unsubscribe, ensure D3 cleans up DOM elements.
                renderBlocks([]);
            },
        })
    );
}

export default renderBlocksInGraph;
